import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, FormControl,
    FormHelperText, FormLabel, Input, Modal, ModalDialog, Sheet, Stack,
    Switch, Table, Textarea, Typography,
} from '@mui/joy';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';

import { useAdmin } from '../../../AdminContext';
import {
    APIAdminAdsCandidates, APIAdminAdsInvalidateCache,
    APIAdminAdsPlacementUpdate, APIAdminAdsPlacements, adminAdsError,
} from '../../../api_admin_ads';
import { FORMAT_LABELS, formatMoney } from '../../../components/Ads/adsConstants';


function PlacementEditModal({ open, placement, onClose, onDone }) {
    const { adminToken: token } = useAdmin();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && placement) {
            setForm({
                name: placement.name,
                description: placement.description || '',
                screen_hint: placement.screen_hint || '',
                is_active: placement.is_active,
                min_cpc: placement.min_cpc,
                min_cpm: placement.min_cpm,
                max_ads_per_request: placement.max_ads_per_request,
                default_freq_per_day: placement.default_freq_per_day,
                default_freq_interval_sec: placement.default_freq_interval_sec,
                order: placement.order,
            });
            setError(null);
        }
    }, [open, placement]);

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const submit = async () => {
        try {
            setSaving(true);
            setError(null);
            await APIAdminAdsPlacementUpdate(token, placement.id, {
                ...form,
                min_cpc: Number(form.min_cpc),
                min_cpm: Number(form.min_cpm),
                max_ads_per_request: Number(form.max_ads_per_request),
                default_freq_per_day: Number(form.default_freq_per_day),
                default_freq_interval_sec: Number(form.default_freq_interval_sec),
                order: Number(form.order),
            });
            onDone(`Площадка «${form.name}» обновлена`);
            onClose();
        } catch (e) {
            setError(adminAdsError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={saving ? undefined : onClose}>
            <ModalDialog sx={{ width: { xs: '94vw', sm: 520 }, maxHeight: '92vh', overflowY: 'auto' }}>
                <Typography level="title-lg">Площадка · {placement?.code}</Typography>
                {error && <Alert color="danger" sx={{ mb: 1.5 }}>{error}</Alert>}

                <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <FormControl>
                        <FormLabel>Название</FormLabel>
                        <Input value={form.name || ''} onChange={(e) => set({ name: e.target.value })} />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Описание для рекламодателя</FormLabel>
                        <Textarea minRows={2} value={form.description || ''}
                                  onChange={(e) => set({ description: e.target.value })} />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Где показывается</FormLabel>
                        <Input value={form.screen_hint || ''}
                               onChange={(e) => set({ screen_hint: e.target.value })} />
                    </FormControl>

                    <Divider />

                    <Stack direction="row" spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Мин. ставка за клик, ₽</FormLabel>
                            <Input type="number" value={form.min_cpc ?? ''}
                                   onChange={(e) => set({ min_cpc: e.target.value })} />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Мин. ставка за 1000, ₽</FormLabel>
                            <Input type="number" value={form.min_cpm ?? ''}
                                   onChange={(e) => set({ min_cpm: e.target.value })} />
                        </FormControl>
                    </Stack>
                    <FormHelperText sx={{ mt: -1 }}>
                        Повышение минимума не затронет уже работающие кампании с меньшей ставкой —
                        только новые и переотправленные на модерацию
                    </FormHelperText>

                    <Stack direction="row" spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Объявлений за запрос</FormLabel>
                            <Input type="number" value={form.max_ads_per_request ?? ''}
                                   onChange={(e) => set({ max_ads_per_request: e.target.value })} />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Порядок</FormLabel>
                            <Input type="number" value={form.order ?? ''}
                                   onChange={(e) => set({ order: e.target.value })} />
                        </FormControl>
                    </Stack>

                    <Stack direction="row" spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Показов на пользователя в сутки</FormLabel>
                            <Input type="number" value={form.default_freq_per_day ?? ''}
                                   onChange={(e) => set({ default_freq_per_day: e.target.value })} />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Мин. интервал, сек</FormLabel>
                            <Input type="number" value={form.default_freq_interval_sec ?? ''}
                                   onChange={(e) => set({ default_freq_interval_sec: e.target.value })} />
                        </FormControl>
                    </Stack>

                    <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                        <FormLabel>Площадка активна</FormLabel>
                        <Switch checked={!!form.is_active}
                                onChange={(e) => set({ is_active: e.target.checked })} />
                    </FormControl>

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button variant="outlined" color="neutral" fullWidth onClick={onClose} disabled={saving}>
                            Отмена
                        </Button>
                        <Button fullWidth loading={saving} onClick={submit}>Сохранить</Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


/** Диагностика: сколько объявлений сейчас в пуле кандидатов площадки. */
function CandidatesPanel({ placements }) {
    const { adminToken: token, has } = useAdmin();
    const [result, setResult] = useState({});
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);

    const check = async (code) => {
        setBusy(code);
        setError(null);
        try {
            const res = await APIAdminAdsCandidates(token, code);
            setResult((prev) => ({ ...prev, [code]: res }));
        } catch (e) {
            setError(adminAdsError(e));
        } finally {
            setBusy(null);
        }
    };

    const invalidate = async () => {
        setBusy('invalidate');
        try {
            await APIAdminAdsInvalidateCache(token);
            setResult({});
        } catch (e) {
            setError(adminAdsError(e));
        } finally {
            setBusy(null);
        }
    };

    return (
        <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md', mt: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <BugReportRoundedIcon />
                <Typography level="title-sm">Диагностика выдачи</Typography>
            </Stack>
            <Typography level="body-sm" color="neutral" sx={{ mb: 1.5 }}>
                Первое, что нужно проверить, когда партнёр говорит «моя реклама
                не показывается»: попадают ли его объявления в пул кандидатов площадки.
                Пул кэшируется на минуту — при подозрении на устаревшие данные сбросьте кэш.
            </Typography>

            {error && <Alert color="danger" sx={{ mb: 1.5 }}>{error}</Alert>}

            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                {placements.map((placement) => (
                    <Button
                        key={placement.code}
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        loading={busy === placement.code}
                        onClick={() => check(placement.code)}
                    >
                        {placement.code}
                        {result[placement.code] && (
                            <Chip size="sm" variant="soft"
                                  color={result[placement.code].count > 0 ? 'success' : 'danger'}
                                  sx={{ ml: 0.8 }}>
                                {result[placement.code].count}
                            </Chip>
                        )}
                    </Button>
                ))}
                {has('ads.manage') && (
                    <Button size="sm" variant="soft" color="warning"
                            startDecorator={<RefreshRoundedIcon />}
                            loading={busy === 'invalidate'}
                            onClick={invalidate}>
                        Сбросить кэш кандидатов
                    </Button>
                )}
            </Stack>
        </Sheet>
    );
}


export default function AdminAdsPlacementsPage() {
    const { adminToken: token, has } = useAdmin();
    const canManage = has('ads.manage');

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [editTarget, setEditTarget] = useState(null);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdminAdsPlacements(token);
            setItems(res?.items || []);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось загрузить площадки'));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box>
            <Typography level="h4" sx={{ mb: 0.5 }}>Рекламные площадки</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Места размещения в приложении. Выключенная площадка сразу
                перестаёт отдавать объявления, кампании при этом не останавливаются.
            </Typography>

            {notice && <Alert color="success" sx={{ mb: 2 }}>{notice}</Alert>}
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table hoverRow sx={{ '--TableCell-paddingY': '10px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 150 }}>Код</th>
                                <th>Название</th>
                                <th style={{ width: 170 }}>Формат</th>
                                <th style={{ width: 100 }}>Мин. CPC</th>
                                <th style={{ width: 100 }}>Мин. CPM</th>
                                <th style={{ width: 110 }}>Базовый CTR</th>
                                <th style={{ width: 100 }}>Активна</th>
                                <th style={{ width: 110 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((placement) => (
                                <tr key={placement.id}>
                                    <td>
                                        <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                            {placement.code}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                            {placement.name}
                                        </Typography>
                                        <Typography level="body-xs" color="neutral">
                                            {placement.screen_hint}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Chip size="sm" variant="outlined">
                                            {FORMAT_LABELS[placement.format] || placement.format}
                                        </Chip>
                                    </td>
                                    <td>{formatMoney(placement.min_cpc)} ₽</td>
                                    <td>{formatMoney(placement.min_cpm)} ₽</td>
                                    <td>
                                        {placement.baseline_ctr != null
                                            ? `${(Number(placement.baseline_ctr) * 100).toFixed(2)}%`
                                            : '—'}
                                    </td>
                                    <td>
                                        <Chip size="sm" variant="soft"
                                              color={placement.is_active ? 'success' : 'neutral'}>
                                            {placement.is_active ? 'да' : 'нет'}
                                        </Chip>
                                    </td>
                                    <td>
                                        {canManage && (
                                            <Button size="sm" variant="soft"
                                                    onClick={() => setEditTarget(placement)}>
                                                Изменить
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            {!loading && <CandidatesPanel placements={items} />}

            <PlacementEditModal
                open={!!editTarget}
                placement={editTarget}
                onClose={() => setEditTarget(null)}
                onDone={(message) => { setNotice(message); load(); }}
            />
        </Box>
    );
}